import {
  Cube,
  Package,
  ShoppingCart,
  TrendDown,
  TrendUp,
  Truck,
  Warning,
} from "@phosphor-icons/react"
import {
  HoverCard,
  HoverCardContent,
  HoverCardFooter,
  HoverCardHeader,
  HoverCardTitle,
  HoverCardTrigger,
} from "~/components/reusable/hovercard/HoverCard"
import HoverCardRow from "~/components/reusable/hovercard/HoverCardRow"
import HoverCardRowWrapper from "~/components/reusable/hovercard/HoverCardRowWrapper"
import { formatNumber } from "~/utils/formatting"
import { formatName } from "~/utils/namingUtils"

export type MonthCoverVariant = "current" | "withPO" | "withPOMinusLeadtime"

interface Props {
  variant: MonthCoverVariant
  value: number | null
  salesL12M: number
  stockLevel: number
  /** Available-to-Sell, as reported by the tenant. When present, the "current"
   *  Month cover is based on this instead of raw on-hand stock. */
  availableToSell?: number
  /** Whether the tenant reports ATS at all (some SKU in view carries it). Gates the
   *  "missing ATS" warning so it only shows for tenants that actually use ATS. */
  atsAvailable?: boolean
  orderQuantity?: number
  leadtimeDays?: number
  /** Incoming orders arriving before the order's ETA — breaks down stock at arrival */
  incomingStock?: number
  /** Expected demand until the order arrives: monthly L12M avg × months until arrival,
   *  capped at current + incoming stock. */
  demandBeforeArrival?: number
  monthsUntilArrival?: number
}

const TITLES: Record<MonthCoverVariant, string> = {
  current: "Month cover",
  withPO: "Month cover w. PO",
  withPOMinusLeadtime: "Month cover w. PO - LT",
}

const SUBTITLES: Record<MonthCoverVariant, string> = {
  current: "Months of stock at today's run-rate",
  withPO: "Months of stock at PO arrival, incl. this order",
  withPOMinusLeadtime: "Cover w. PO, minus the leadtime in months",
}

const units = (n: number) => `${formatNumber(n)} units`
const months = (n: number) => `${n.toFixed(1)} months`

export function MonthCoverField(props: Props) {
  const { value } = props
  return (
    <HoverCard closeDelay={300}>
      <HoverCardTrigger className="flex h-full w-full items-center">
        {value == null ? "-" : value.toFixed(1)}
      </HoverCardTrigger>
      <HoverCardContent className="w-90 p-0" align="start">
        <MonthCoverFieldContent {...props} />
      </HoverCardContent>
    </HoverCard>
  )
}

function MonthCoverFieldContent({
  variant,
  value,
  salesL12M,
  stockLevel,
  availableToSell,
  atsAvailable,
  orderQuantity,
  leadtimeDays,
  incomingStock,
  demandBeforeArrival,
  monthsUntilArrival,
}: Props) {
  const monthlySales = salesL12M / 12
  const monthlySalesLabel = `Monthly ${formatName("sale", true)}`

  // The "current" cover trades on Available-to-Sell when the tenant sends it; fall back
  // to raw on-hand stock otherwise.
  const currentStock = availableToSell ?? stockLevel
  const currentStockLabel =
    availableToSell != null ? formatName("available_quantity", false, true) : "Current stock"
  // Highlight the fallback: no ATS for this SKU means the "current" cover is computed off
  // closing stock, which overstates availability when stock is reserved/allocated.
  const atsMissing = variant === "current" && availableToSell == null && atsAvailable === true

  // Stock at arrival is current stock + incoming before ETA - expected demand until
  // arrival (monthly L12M avg × months until arrival, capped at what's sellable).
  const stockAtArrival = stockLevel + (incomingStock ?? 0) - (demandBeforeArrival ?? 0)
  const totalStockAtArrival = stockAtArrival + (orderQuantity ?? 0)
  // Numerator of the cover for this variant — the figure the footer divides.
  const numerator = variant === "current" ? currentStock : totalStockAtArrival
  const baseCover = monthlySales > 0 ? numerator / monthlySales : null
  const leadtimeMonths = (leadtimeDays ?? 0) / 30

  const canBreakDownArrival = demandBeforeArrival != null

  const header = (
    <HoverCardHeader className="flex-col items-start h-auto">
      <HoverCardTitle>{TITLES[variant]}</HoverCardTitle>
      <p className="paragraph-sm text-fg-muted">{SUBTITLES[variant]}</p>
    </HoverCardHeader>
  )

  if (value == null) {
    return (
      <>
        {header}
        <div className="px-4 py-3 paragraph-sm text-fg-muted">
          No {formatName("sale", true)} in the last 12 months, so cover can&apos;t be calculated.
        </div>
      </>
    )
  }

  // Literal arithmetic with the real numbers substituted, mirroring the GMROI breakdown.
  const calc =
    variant === "withPOMinusLeadtime"
      ? `${(baseCover ?? 0).toFixed(1)} - ${leadtimeMonths.toFixed(1)} = ${value.toFixed(1)}`
      : `${formatNumber(numerator)} / ${formatNumber(monthlySales, 1)} = ${value.toFixed(1)}`

  return (
    <>
      {header}

      {variant === "current" ? (
        <HoverCardRowWrapper>
          <HoverCardRow
            icon={
              atsMissing ? (
                <Warning size={14} className="text-badge-orange-fg-text" />
              ) : (
                <Package size={14} />
              )
            }
            label={currentStockLabel}
            value={units(currentStock)}
          />
          {atsMissing && (
            <div className="flex items-start gap-2 px-4 pb-2 text-fg-muted">
              <p className="paragraph-sm">
                No availability data for this SKU. Cover is based on current stock, which may
                overstate what&apos;s sellable.
              </p>
            </div>
          )}
        </HoverCardRowWrapper>
      ) : (
        <>
          <HoverCardRowWrapper>
            {canBreakDownArrival && (
              <>
                <HoverCardRow
                  icon={<Cube size={14} />}
                  label="Current stock"
                  value={units(stockLevel)}
                />
                {(incomingStock ?? 0) > 0 && (
                  <HoverCardRow
                    icon={<Truck size={14} />}
                    label="Incoming orders"
                    value={`+${units(incomingStock ?? 0)}`}
                    className="text-green-500"
                  />
                )}
                <HoverCardRow
                  icon={<TrendUp size={14} />}
                  label="Demand before arrival"
                  description={
                    monthsUntilArrival != null
                      ? `${monthsUntilArrival.toFixed(1)} mo × ${formatNumber(monthlySales, 1)}/mo (L12M avg)`
                      : undefined
                  }
                  value={`-${units(demandBeforeArrival ?? 0)}`}
                  className="text-red-500"
                />
              </>
            )}
            <HoverCardRow
              icon={<ShoppingCart size={14} />}
              label="This order"
              value={`+${units(orderQuantity ?? 0)}`}
              className={(orderQuantity ?? 0) > 0 ? "text-green-500" : undefined}
            />
          </HoverCardRowWrapper>
          <div className="border-t border-base">
            <HoverCardRowWrapper>
              <HoverCardRow
                icon={<Package size={14} />}
                label="= Total at arrival"
                value={units(totalStockAtArrival)}
              />
            </HoverCardRowWrapper>
          </div>
        </>
      )}

      <div className="border-t border-base">
        <HoverCardRowWrapper>
          <HoverCardRow
            icon={<TrendDown size={14} />}
            label={monthlySalesLabel}
            value={`${formatNumber(monthlySales, 1)} (L12M ${formatNumber(salesL12M)})`}
          />
        </HoverCardRowWrapper>
      </div>

      {variant === "withPOMinusLeadtime" && (
        <div className="border-t border-base">
          <HoverCardRowWrapper>
            <HoverCardRow
              icon={<Package size={14} />}
              label="Cover before leadtime"
              value={baseCover == null ? "—" : months(baseCover)}
            />
            <HoverCardRow
              icon={<Truck size={14} />}
              label="Leadtime"
              value={`${formatNumber(leadtimeDays ?? 0)} days (${leadtimeMonths.toFixed(1)} mo)`}
            />
          </HoverCardRowWrapper>
        </div>
      )}

      <div className="border-t border-base">
        <HoverCardRowWrapper>
          <HoverCardRow
            icon={<Package size={14} />}
            label={TITLES[variant]}
            value={months(value)}
          />
        </HoverCardRowWrapper>
      </div>

      <HoverCardFooter className="bg-subtle">
        <p className="paragraph-sm text-fg-muted font-mono">{calc}</p>
      </HoverCardFooter>
    </>
  )
}
